import * as React from 'react';

import Uri from 'jsuri';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';

import { ON_SITE_SEARCH_PATH, SEO_ENTITY_SEARCH_PATH } from 'bundles/search-common/constants';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import {
  getAbsoluteNextPageHref,
  getAbsolutePrevPageHref,
  getCanonicalLinkHrefOverride,
} from 'bundles/search-common/utils/seoUtils';
import type { LinkInfo } from 'bundles/seo/CourseraMetatagsV2Types';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/search-common';

export type PropsFromCaller = {
  hits?: SearchProductHit[];
  totalPages?: number;
};

type PropsToComponent = PropsFromCaller & {
  location: InjectedRouter['location'];
  queryParam: string;
  otherParamsLength: number;
};

const filterQueryParam = (s: string) => s !== 'query';

export const getMetaTitle = (topic: string) => {
  if (topic) {
    const metaTitleValue = _t('Top #{topic} Courses - Learn #{topic} Online', { topic });
    return metaTitleValue;
  }
  return _t('Courses');
};

export const shouldDisableCrawlerIndex = (searchQuery: string, pathname: string) => {
  // Allow robots to index /courses path.
  if (pathname === SEO_ENTITY_SEARCH_PATH && !searchQuery) {
    return false;
  }

  // No index on site search path, favor seo entity path.
  return !searchQuery || pathname === ON_SITE_SEARCH_PATH;
};

export const getDescription = (topic: string, hits?: SearchProductHit[]) => {
  if (hits) {
    const sampleHitNames = hits
      .slice(0, 2)
      .map((hit) => hit.name)
      .join(' and ');

    return _t(
      '#{topic} courses from top universities and industry leaders. Learn #{topic} online with courses like #{sampleHitNames}.',
      {
        topic,
        sampleHitNames,
      }
    );
  } else {
    return _t('#{topic} courses from top universities and industry leaders.', {
      topic,
    });
  }
};

export function generateHreflangHref(href: string, searchQuery: string): string {
  const newUri = new Uri(href).addQueryParam('query', searchQuery);
  return newUri.toString();
}

/**
 * Writes metatags [see https://www.wordstream.com/meta-tags for a quick intro]
 * We attempt to create unique metatags for each search result page, using some heuristic logics based
 * on the results of the search.
 */
const SearchSEOMetatagsWriter = ({ location, otherParamsLength, queryParam, hits, totalPages }: PropsToComponent) => {
  if (!location) {
    // If we do not have a hostname, this when React context is not available, which happens with
    // Algolia's SSR implementation to get the initial reesults to show, so we ignore this case.
    return <div />;
  }

  const queryFromSearch: string | undefined = Array.isArray(location.query?.query)
    ? location.query?.query.join(' ')
    : location.query?.query;

  // Capitalize each word in the search query.
  const topic = queryFromSearch
    ? queryFromSearch
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';
  const metaDescription = getDescription(topic, hits);

  // Disable Canonical Link Override for Entity pages.
  const isEntityPage = location?.pathname === SEO_ENTITY_SEARCH_PATH;

  // Do not show hreflang for /search OR when there are other params other than "query"
  const shouldNotHaveHrefLang = !isEntityPage || otherParamsLength > 0;
  function linkMapper(link: LinkInfo): LinkInfo {
    if (queryFromSearch) {
      return { ...link, href: generateHreflangHref(link.href, queryFromSearch) };
    } else {
      return link;
    }
  }

  const pageNumber = location.query?.page ? Number(location.query?.page) : 1;

  return (
    <CourseraMetatags
      metaNameAndProperties={{
        title: getMetaTitle(topic),
        description: metaDescription,
        disableCourseraSuffix: true,
        disableCourseraDescriptionSuffix: true,
        disableCrawlerIndexing: shouldDisableCrawlerIndex(queryParam, location?.pathname),
      }}
      paginationProperties={{
        canonicalLinkHrefOverride: !isEntityPage
          ? getCanonicalLinkHrefOverride({ searchQuery: queryFromSearch || '', location })
          : getCanonicalLinkHrefOverride({
              searchQuery: queryFromSearch || '',
              location,
              pageNumber,
            }),
        absoluteNextPageHref: isEntityPage
          ? getAbsoluteNextPageHref({
              searchQuery: queryFromSearch || '',
              location,
              numTotalPages: totalPages,
              pageNumber,
            })
          : undefined,
        absolutePrevPageHref: isEntityPage
          ? getAbsolutePrevPageHref({
              searchQuery: queryFromSearch || '',
              location,
              pageNumber,
            })
          : undefined,
      }}
      hrefLangOverrides={(links) => (shouldNotHaveHrefLang ? [] : links.map(linkMapper))}
    />
  );
};

export default connectToRouter<PropsToComponent, PropsFromCaller>((router) => ({
  otherParamsLength: Object.keys(router?.location?.query).filter(filterQueryParam).length,
  queryParam: router?.location?.query?.query || '',
  location: router?.location,
}))(SearchSEOMetatagsWriter);
